<template>
  <div class="dispatch-dashboard" v-loading="loading">
    <!-- 顶栏 -->
    <header class="dash-header">
      <div class="dash-header-left">
        <h1>调度控制台</h1>
        <span class="dash-date">{{ todayLabel }}</span>
      </div>
      <div class="dash-header-right">
        <a-button icon="reload" size="small" @click="loadDashboard">刷新</a-button>
        <a-badge :count="alertCount" :overflow-count="9">
          <a-button icon="bell" size="small" shape="circle" />
        </a-badge>
        <span class="dash-clock">{{ clockText }}</span>
      </div>
    </header>

    <!-- 主 KPI -->
    <div class="kpi-row primary-kpi">
      <div
        v-for="card in primaryKpis"
        :key="card.key"
        class="kpi-card"
        :class="'kpi-' + card.key"
        @click="onKpiClick(card)"
      >
        <div class="kpi-icon-wrap"><a-icon :type="kpiIcon(card.key)" /></div>
        <div class="kpi-body">
          <p class="kpi-value">{{ card.value }}<span v-if="card.unit" class="kpi-unit">{{ card.unit }}</span></p>
          <p class="kpi-label">{{ card.label }}</p>
          <p class="kpi-trend" :class="'trend-' + card.trend">{{ card.delta }} {{ card.deltaLabel }}</p>
        </div>
      </div>
    </div>

    <!-- 次 KPI -->
    <div class="kpi-row secondary-kpi">
      <div v-for="card in secondaryKpis" :key="card.key" class="kpi-card kpi-secondary">
        <div class="kpi-body">
          <p class="kpi-label-sm">{{ card.label }}</p>
          <p class="kpi-value-sm">
            <template v-if="card.key === 'revenue'">¥ </template>{{ card.value }}<span class="kpi-unit-sm">{{ card.unit }}</span>
          </p>
          <p class="kpi-trend-sm" :class="'trend-' + card.trend">{{ card.delta }} {{ card.deltaLabel }}</p>
        </div>
      </div>
    </div>

    <!-- 主体三栏 -->
    <div class="dash-main">
      <div class="dash-col dash-col-left">
        <section class="dash-card">
          <h3>订单状态分布</h3>
          <div ref="statusChart" class="chart-box chart-pie" />
        </section>
        <section class="dash-card">
          <h3>护工等级分布</h3>
          <div class="level-bars">
            <div v-for="item in caregiverLevels" :key="item.name" class="level-bar-row">
              <span class="level-name">{{ item.name }}</span>
              <div class="level-track">
                <div class="level-fill" :style="{ width: levelPercent(item) + '%', background: item.color }" />
              </div>
              <span class="level-count">{{ item.value }}</span>
            </div>
          </div>
        </section>
      </div>

      <div class="dash-col dash-col-center">
        <section class="dash-card">
          <h3>近 7 天订单趋势</h3>
          <div ref="trendChart" class="chart-box chart-bar" />
        </section>
        <div class="center-bottom">
          <section class="dash-card fulfillment-card">
            <h3>近 7 天履约率</h3>
            <div ref="gaugeChart" class="chart-box chart-gauge" />
            <div class="fulfillment-meta">
              <span>完工 <strong>{{ fulfillment.completed }}</strong> 单</span>
              <span>总计 <strong>{{ fulfillment.total }}</strong> 单</span>
              <span>目标 <strong>{{ fulfillment.goal }}%</strong></span>
            </div>
          </section>
          <section class="dash-card">
            <h3>医院订单分布</h3>
            <div v-if="!hospitalStats.length" class="empty-hint">暂无订单数据</div>
            <div v-else class="hospital-bars">
              <div v-for="h in hospitalStats" :key="h.name" class="hospital-row">
                <span class="hospital-name" :title="h.name">{{ h.name }}</span>
                <div class="hospital-track">
                  <div class="hospital-fill" :style="{ width: h.percent + '%' }" />
                </div>
                <span class="hospital-count">{{ h.count }}</span>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div class="dash-col dash-col-right">
        <section class="dash-card">
          <div class="card-head-row">
            <h3>在线护工</h3>
            <a-badge :count="onlineCaregivers.length" :number-style="{ backgroundColor: '#22c55e' }" />
          </div>
          <div class="caregiver-list">
            <div v-for="cg in onlineCaregivers" :key="cg.id" class="cg-row">
              <div class="cg-avatar" :class="'lv-' + cg.levelColor">{{ cg.name.charAt(0) }}</div>
              <div class="cg-info">
                <p class="cg-name">{{ cg.name }} <span class="cg-level" :class="'lv-' + cg.levelColor">{{ cg.level }}</span></p>
                <p class="cg-status" :class="cg.status === 'busy' ? 'busy' : 'idle'">
                  {{ cg.statusLabel }}<template v-if="cg.serviceHours"> · 累计 {{ cg.serviceHours }}h</template>
                </p>
              </div>
            </div>
            <div v-if="!onlineCaregivers.length" class="empty-hint">暂无在线护工</div>
          </div>
        </section>
        <section class="dash-card">
          <h3>最近动态</h3>
          <ul class="activity-list">
            <li v-for="(act, idx) in activities" :key="idx" class="activity-item">
              <span class="activity-icon" :class="'act-' + act.type">
                <a-icon :type="activityIcon(act.type)" />
              </span>
              <div class="activity-body">
                <p>{{ act.text }}</p>
                <span>{{ act.time }}</span>
              </div>
            </li>
            <li v-if="!activities.length" class="empty-hint">暂无动态</li>
          </ul>
        </section>
      </div>
    </div>

    <!-- 运营告警 -->
    <section class="dash-card alert-section">
      <div class="card-head-row">
        <h3>运营告警</h3>
        <a-badge :count="alerts.length" />
      </div>
      <div class="alert-grid">
        <div v-for="(alert, idx) in alerts" :key="idx" class="alert-card" :class="'alert-' + alert.level">
          <div class="alert-icon"><a-icon :type="alertIcon(alert.level)" /></div>
          <div class="alert-body">
            <p class="alert-title">{{ alert.title }}</p>
            <p class="alert-desc">{{ alert.description }}</p>
            <span class="alert-time">{{ alert.time }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { dispatchApi } from "./dispatchApi";

export default {
  name: "DispatchConsole",
  data() {
    return {
      loading: false,
      clockText: "",
      clockTimer: null,
      primaryKpis: [],
      secondaryKpis: [],
      orderStatusSlices: [],
      caregiverLevels: [],
      orderTrend: [],
      fulfillment: { rate: 0, goal: 95, completed: 0, total: 0 },
      hospitalStats: [],
      onlineCaregivers: [],
      activities: [],
      alerts: [],
      charts: {},
    };
  },
  computed: {
    todayLabel() {
      const d = new Date();
      const week = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"];
      return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${week[d.getDay()]}`;
    },
    alertCount() {
      return this.alerts.filter((a) => a.level === "critical").length;
    },
    maxLevelValue() {
      const vals = this.caregiverLevels.map((x) => x.value);
      return Math.max(...vals, 1);
    },
  },
  mounted() {
    this.tickClock();
    this.clockTimer = setInterval(this.tickClock, 1000);
    this.loadDashboard();
    window.addEventListener("resize", this.resizeCharts);
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer);
    window.removeEventListener("resize", this.resizeCharts);
    Object.values(this.charts).forEach((c) => c && c.dispose());
  },
  methods: {
    tickClock() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      this.clockText = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    async loadDashboard() {
      this.loading = true;
      try {
        const data = await dispatchApi.dashboard();
        this.primaryKpis = data.primaryKpis || [];
        this.secondaryKpis = data.secondaryKpis || [];
        this.orderStatusSlices = data.orderStatusSlices || [];
        this.caregiverLevels = data.caregiverLevels || [];
        this.orderTrend = data.orderTrend || [];
        this.fulfillment = data.fulfillment || this.fulfillment;
        this.hospitalStats = data.hospitalStats || [];
        this.onlineCaregivers = data.onlineCaregivers || [];
        this.activities = data.activities || [];
        this.alerts = data.alerts || [];
        this.$nextTick(this.renderCharts);
      } catch (e) {
        this.$message.error(e.message || "仪表盘加载失败");
      } finally {
        this.loading = false;
      }
    },
    kpiIcon(key) {
      const map = {
        pending: "file-text",
        serving: "heart",
        online: "team",
        doneToday: "check-circle",
      };
      return map[key] || "dashboard";
    },
    activityIcon(type) {
      const map = { assign: "user-add", done: "check", review: "star" };
      return map[type] || "info-circle";
    },
    alertIcon(level) {
      const map = { critical: "warning", warning: "exclamation-circle", info: "info-circle" };
      return map[level] || "info-circle";
    },
    levelPercent(item) {
      return Math.round((item.value / this.maxLevelValue) * 100);
    },
    onKpiClick(card) {
      if (card.key === "pending" || card.key === "serving" || card.key === "doneToday") {
        this.$router.push("/dispatch/orders");
      } else if (card.key === "online") {
        this.$router.push("/dispatch/caregivers");
      }
    },
    renderCharts() {
      this.renderStatusChart();
      this.renderTrendChart();
      this.renderGaugeChart();
    },
    renderStatusChart() {
      const el = this.$refs.statusChart;
      if (!el) return;
      if (this.charts.status) this.charts.status.dispose();
      const chart = echarts.init(el);
      this.charts.status = chart;
      const data = this.orderStatusSlices.filter((s) => s.value > 0);
      chart.setOption({
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: { bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11 } },
        color: data.map((d) => d.color),
        series: [{
          type: "pie",
          radius: ["42%", "68%"],
          center: ["50%", "45%"],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 2 },
          label: { show: false },
          data: data.length ? data.map((d) => ({ name: d.name, value: d.value })) : [{ name: "暂无", value: 1 }],
        }],
      });
    },
    renderTrendChart() {
      const el = this.$refs.trendChart;
      if (!el) return;
      if (this.charts.trend) this.charts.trend.dispose();
      const chart = echarts.init(el);
      this.charts.trend = chart;
      const labels = this.orderTrend.map((d) => d.label.split(" ")[0]);
      chart.setOption({
        tooltip: { trigger: "axis" },
        legend: { data: ["新增", "完工"], top: 0, right: 0, textStyle: { fontSize: 11 } },
        grid: { left: 36, right: 12, top: 32, bottom: 24 },
        xAxis: { type: "category", data: labels, axisLabel: { fontSize: 10 } },
        yAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10 } },
        series: [
          { name: "新增", type: "bar", barMaxWidth: 18, itemStyle: { color: "#3b82f6", borderRadius: [3, 3, 0, 0] },
            data: this.orderTrend.map((d) => d.newOrders) },
          { name: "完工", type: "bar", barMaxWidth: 18, itemStyle: { color: "#22c55e", borderRadius: [3, 3, 0, 0] },
            data: this.orderTrend.map((d) => d.doneOrders) },
        ],
      });
    },
    renderGaugeChart() {
      const el = this.$refs.gaugeChart;
      if (!el) return;
      if (this.charts.gauge) this.charts.gauge.dispose();
      const chart = echarts.init(el);
      this.charts.gauge = chart;
      chart.setOption({
        series: [{
          type: "gauge",
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          radius: "90%",
          center: ["50%", "58%"],
          progress: { show: true, width: 14, itemStyle: { color: "#0082d4" } },
          axisLine: { lineStyle: { width: 14, color: [[1, "#e2e8f0"]] } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          pointer: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 28,
            fontWeight: 700,
            color: "#0f172a",
            offsetCenter: [0, "10%"],
            formatter: "{value}%",
          },
          data: [{ value: this.fulfillment.rate || 0 }],
        }],
      });
    },
    resizeCharts() {
      Object.values(this.charts).forEach((c) => c && c.resize());
    },
  },
};
</script>

<style scoped>
.dispatch-dashboard {
  padding: 1rem 1.25rem 1.5rem;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
  background: #f1f5f9;
}

.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}
.dash-header-left {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
}
.dash-header-left h1 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
}
.dash-date {
  font-size: 0.8125rem;
  color: #64748b;
}
.dash-header-right {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.dash-clock {
  font-family: ui-monospace, monospace;
  font-size: 0.875rem;
  color: #475569;
  min-width: 4.5rem;
}

.kpi-row {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.primary-kpi {
  grid-template-columns: repeat(4, 1fr);
}
.secondary-kpi {
  grid-template-columns: repeat(4, 1fr);
}
.kpi-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.125rem;
  display: flex;
  gap: 0.875rem;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}
.kpi-card:hover {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}
.kpi-secondary {
  cursor: default;
  padding: 0.875rem 1rem;
}
.kpi-secondary:hover {
  transform: none;
}
.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}
.kpi-pending .kpi-icon-wrap { background: #fef2f2; color: #ef4444; }
.kpi-serving .kpi-icon-wrap { background: #eff6ff; color: #3b82f6; }
.kpi-online .kpi-icon-wrap { background: #f0fdf4; color: #22c55e; }
.kpi-doneToday .kpi-icon-wrap { background: #f8fafc; color: #64748b; }

.kpi-value {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
}
.kpi-unit { font-size: 0.875rem; font-weight: 600; margin-left: 2px; }
.kpi-label { margin: 0.25rem 0 0; font-size: 0.8125rem; color: #64748b; }
.kpi-trend { margin: 0.375rem 0 0; font-size: 0.6875rem; font-weight: 600; }
.trend-up { color: #16a34a; }
.trend-down { color: #dc2626; }
.trend-flat { color: #94a3b8; }

.kpi-label-sm { margin: 0; font-size: 0.75rem; color: #94a3b8; }
.kpi-value-sm { margin: 0.25rem 0 0; font-size: 1.25rem; font-weight: 700; color: #0f172a; }
.kpi-unit-sm { font-size: 0.75rem; color: #64748b; margin-left: 2px; }
.kpi-trend-sm { margin: 0.25rem 0 0; font-size: 0.6875rem; font-weight: 600; }

.dash-main {
  display: grid;
  grid-template-columns: 280px 1fr 280px;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  align-items: stretch;
}
.dash-col {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
}
.dash-col-left > .dash-card:last-child,
.dash-col-right > .dash-card:last-child {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.dash-col-center {
  flex: 1;
}
.dash-col-center > .dash-card:first-child {
  flex-shrink: 0;
}
.center-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  flex: 1;
  align-items: stretch;
  min-height: 0;
}
.center-bottom .dash-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.fulfillment-card {
  justify-content: space-between;
}

.dash-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1rem 1.125rem;
}
.dash-card h3 {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #0f172a;
}
.card-head-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.card-head-row h3 { margin: 0; }

.chart-box { width: 100%; }
.chart-pie { height: 220px; }
.chart-bar { height: 200px; }
.chart-gauge { height: 140px; }

.level-bars { display: flex; flex-direction: column; gap: 0.625rem; flex: 1; justify-content: center; }
.level-bar-row {
  display: grid;
  grid-template-columns: 72px 1fr 28px;
  align-items: center;
  gap: 0.5rem;
}
.level-name { font-size: 0.75rem; color: #64748b; }
.level-track { height: 8px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
.level-fill { height: 100%; border-radius: 999px; transition: width 0.4s; }
.level-count { font-size: 0.75rem; font-weight: 700; color: #0f172a; text-align: right; }

.fulfillment-meta {
  display: flex;
  justify-content: space-around;
  font-size: 0.6875rem;
  color: #64748b;
  margin-top: -0.25rem;
}
.fulfillment-meta strong { color: #0f172a; }

.hospital-bars { display: flex; flex-direction: column; gap: 0.5rem; flex: 1; justify-content: center; }
.hospital-row {
  display: grid;
  grid-template-columns: 80px 1fr 24px;
  align-items: center;
  gap: 0.5rem;
}
.hospital-name {
  font-size: 0.6875rem;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hospital-track { height: 6px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
.hospital-fill { height: 100%; background: linear-gradient(90deg, #0082d4, #38bdf8); border-radius: 999px; }
.hospital-count { font-size: 0.6875rem; font-weight: 600; color: #0f172a; text-align: right; }

.caregiver-list { display: flex; flex-direction: column; gap: 0.5rem; max-height: 220px; overflow-y: auto; }
.cg-row { display: flex; gap: 0.625rem; align-items: center; }
.cg-avatar {
  width: 36px; height: 36px; border-radius: 0.5rem;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-weight: 700; font-size: 0.875rem; flex-shrink: 0;
}
.lv-gold { background: #f59e0b; }
.lv-silver { background: #6366f1; }
.lv-pro { background: #3b82f6; }
.cg-name { margin: 0; font-size: 0.8125rem; font-weight: 600; color: #0f172a; }
.cg-level {
  font-size: 0.625rem; padding: 0 0.375rem; border-radius: 999px;
  color: #fff; font-weight: 600; margin-left: 0.25rem;
}
.cg-status { margin: 0.125rem 0 0; font-size: 0.6875rem; }
.cg-status.idle { color: #16a34a; }
.cg-status.busy { color: #ea580c; }

.activity-list { margin: 0; padding: 0; list-style: none; max-height: 200px; overflow-y: auto; }
.activity-item { display: flex; gap: 0.625rem; padding: 0.5rem 0; border-bottom: 1px solid #f8fafc; }
.activity-item:last-child { border-bottom: none; }
.activity-icon {
  width: 28px; height: 28px; border-radius: 0.5rem;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.75rem;
}
.act-assign { background: #eff6ff; color: #3b82f6; }
.act-done { background: #f0fdf4; color: #16a34a; }
.act-review { background: #fffbeb; color: #d97706; }
.activity-body p { margin: 0; font-size: 0.75rem; color: #334155; line-height: 1.4; }
.activity-body span { font-size: 0.6875rem; color: #94a3b8; }

.alert-section { margin-bottom: 0; }
.alert-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 0.75rem;
}
.alert-card {
  display: flex; gap: 0.75rem; padding: 0.875rem;
  border-radius: 0.625rem; border: 1px solid;
}
.alert-critical { background: #fef2f2; border-color: #fecaca; }
.alert-warning { background: #fffbeb; border-color: #fde68a; }
.alert-info { background: #eff6ff; border-color: #bfdbfe; }
.alert-icon { font-size: 1.125rem; flex-shrink: 0; margin-top: 2px; }
.alert-critical .alert-icon { color: #dc2626; }
.alert-warning .alert-icon { color: #d97706; }
.alert-info .alert-icon { color: #2563eb; }
.alert-title { margin: 0; font-size: 0.8125rem; font-weight: 700; color: #0f172a; }
.alert-desc { margin: 0.25rem 0 0; font-size: 0.75rem; color: #64748b; line-height: 1.5; }
.alert-time { font-size: 0.6875rem; color: #94a3b8; }

.empty-hint {
  text-align: center; padding: 1rem 0;
  font-size: 0.75rem; color: #94a3b8;
}

@media (max-width: 1280px) {
  .dash-main { grid-template-columns: 1fr; }
  .center-bottom { grid-template-columns: 1fr; }
  .primary-kpi, .secondary-kpi { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .primary-kpi, .secondary-kpi { grid-template-columns: 1fr; }
}
</style>
